// The trip's route: on Apple Maps through MapKit JS when a token is set, otherwise drawn on a plain
// backdrop. Pine line with a white casing, a round From marker and a square To marker, labelled.

import { useEffect, useRef, useState } from "react";
import { mapKitToken } from "../../lib/supabase.ts";

type Point = [number, number];

interface MapKitGlobal {
  init(options: { authorizationCallback: (done: (token: string) => void) => void }): void;
  Map: new (element: HTMLElement, options?: Record<string, unknown>) => {
    showItems(items: unknown[], options?: Record<string, unknown>): void;
    destroy(): void;
  };
  Coordinate: new (latitude: number, longitude: number) => unknown;
  PolylineOverlay: new (coordinates: unknown[], options?: Record<string, unknown>) => unknown;
  Style: new (options: Record<string, unknown>) => unknown;
  MarkerAnnotation: new (coordinate: unknown, options?: Record<string, unknown>) => unknown;
  Padding: new (top: number, right: number, bottom: number, left: number) => unknown;
  FeatureVisibility: { Hidden: string };
}

declare global {
  interface Window {
    mapkit?: MapKitGlobal;
    carvenyMapKitReady?: () => void;
  }
}

let mapKitLoading: Promise<MapKitGlobal> | null = null;

function loadMapKit(token: string): Promise<MapKitGlobal> {
  mapKitLoading ??= new Promise((resolve, reject) => {
    window.carvenyMapKitReady = () => {
      const kit = window.mapkit;
      if (!kit) return reject(new Error("MapKit JS didn't load"));
      kit.init({ authorizationCallback: (done) => done(token) });
      resolve(kit);
    };
    const script = document.createElement("script");
    script.src = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.core.js";
    script.crossOrigin = "anonymous";
    script.dataset.callback = "carvenyMapKitReady";
    script.dataset.libraries = "map,overlays,annotations";
    script.onerror = () => reject(new Error("MapKit JS didn't load"));
    document.head.append(script);
  });
  return mapKitLoading;
}

export function RouteMap({ route, start, end, startName, endName }: {
  route: Point[];
  start: Point | null;
  end: Point | null;
  startName: string;
  endName: string;
}) {
  const points: Point[] = route.length >= 2 ? route : [start, end].filter((point): point is Point => point !== null);
  const [useFallback, setUseFallback] = useState(!mapKitToken);
  const element = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapKitToken || points.length === 0 || !element.current) return;
    let map: { destroy(): void } | undefined;
    let cancelled = false;
    loadMapKit(mapKitToken)
      .then((kit) => {
        if (cancelled || !element.current) return;
        const created = new kit.Map(element.current, {
          showsCompass: kit.FeatureVisibility.Hidden,
          showsMapTypeControl: false,
          isRotationEnabled: false,
        });
        map = created;
        const coordinates = points.map(([lat, lon]) => new kit.Coordinate(lat, lon));
        const casing = new kit.PolylineOverlay(coordinates, { style: new kit.Style({ lineWidth: 9, strokeColor: "#FFFFFF", lineCap: "round", lineJoin: "round" }) });
        const line = new kit.PolylineOverlay(coordinates, { style: new kit.Style({ lineWidth: 5, strokeColor: "#1F5F5B", lineCap: "round", lineJoin: "round" }) });
        const first = coordinates[0];
        const last = coordinates.at(-1);
        const markers = [
          new kit.MarkerAnnotation(first, { title: startName, color: "#1F5F5B", glyphText: "" }),
          ...(points.length > 1 ? [new kit.MarkerAnnotation(last, { title: endName, color: "#2F4468", glyphText: "" })] : []),
        ];
        created.showItems([casing, line, ...markers], { padding: new kit.Padding(36, 36, 36, 36) });
      })
      .catch(() => setUseFallback(true));
    return () => {
      cancelled = true;
      map?.destroy();
    };
    // The route is fixed for a trip; redraw only when the trip changes.
  }, [points.length, startName, endName]);

  const label = points.length > 1 ? `Map of the route from ${startName} to ${endName}` : `Map of ${startName}`;
  if (!useFallback) {
    return <div className="cv-map" role="img" aria-label={label}><div ref={element} className="cv-map-kit" /></div>;
  }
  return <DrawnRoute points={points} startName={startName} endName={endName} label={label} />;
}

/** The route on a plain backdrop, fitted to the box with an equirectangular projection. */
function DrawnRoute({ points, startName, endName, label }: { points: Point[]; startName: string; endName: string; label: string }) {
  const width = 420;
  const height = 210;
  const pad = 30;
  if (points.length === 0) {
    return <div className="cv-map" role="img" aria-label="No route recorded"><svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true"><rect width={width} height={height} style={{ fill: "var(--map-land)" }} /></svg></div>;
  }
  const lats = points.map(([lat]) => lat);
  const scale = Math.cos(((Math.min(...lats) + Math.max(...lats)) / 2) * (Math.PI / 180));
  const xs = points.map(([, lon]) => lon * scale);
  const ys = points.map(([lat]) => -lat);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX || 1e-6;
  const spanY = Math.max(...ys) - minY || 1e-6;
  const factor = Math.min((width - pad * 2) / spanX, (height - pad * 2) / spanY);
  const offsetX = (width - spanX * factor) / 2;
  const offsetY = (height - spanY * factor) / 2;
  const projected = xs.map((x, index) => [offsetX + (x - minX) * factor, offsetY + (ys[index]! - minY) * factor] as const);
  const d = projected.map(([x, y], index) => `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const [startX, startY] = projected[0]!;
  const [endX, endY] = projected.at(-1)!;
  const tagStyle = (x: number, y: number): React.CSSProperties => ({
    left: `${Math.min(Math.max((x / width) * 100, 4), 70)}%`,
    top: `${Math.min(Math.max(((y + 12) / height) * 100, 4), 84)}%`,
  });

  return (
    <div className="cv-map" role="img" aria-label={label}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <rect width={width} height={height} style={{ fill: "var(--map-land)" }} />
        <g style={{ fill: "none", stroke: "var(--map-road)", strokeWidth: 3, opacity: 0.8 }}>
          <path d="M0 52 C 140 46, 280 58, 420 50" />
          <path d="M0 160 C 130 154, 300 166, 420 158" />
          <path d="M96 0 C 92 70, 100 140, 94 210" />
          <path d="M322 0 C 328 60, 318 150, 326 210" />
        </g>
        {projected.length > 1 && <>
          <path d={d} style={{ fill: "none", stroke: "#FFFFFF", strokeWidth: 9, strokeLinecap: "round", strokeLinejoin: "round" }} />
          <path d={d} style={{ fill: "none", stroke: "#1F5F5B", strokeWidth: 5, strokeLinecap: "round", strokeLinejoin: "round" }} />
        </>}
        <circle cx={startX} cy={startY} r="7.5" style={{ fill: "#1F5F5B", stroke: "#FFFFFF", strokeWidth: 3 }} />
        {projected.length > 1 && <rect x={endX - 8} y={endY - 8} width="16" height="16" rx="4" style={{ fill: "#2F4468", stroke: "#FFFFFF", strokeWidth: 3 }} />}
      </svg>
      <span className="cv-map-tag" style={tagStyle(startX, startY)}>{startName}</span>
      {projected.length > 1 && <span className="cv-map-tag" style={tagStyle(endX, endY)}>{endName}</span>}
    </div>
  );
}
